import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import _ from 'underscore';

import Paper from 'components/paper';
import Select from 'components/select';
import TextField from 'components/text-field';
import ToggleButton from 'components/toggle-button';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { Alert, Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import { GridRowsProp } from '@mui/x-data-grid';
import Breadcrumbs from 'components/breadcrumbs';

import RadioSelectItem from 'components/radio-select-item';
import { OwnerType } from 'types/shared/owner-type.enum';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import FeedStrategiesGrid from 'views/profiles-strategies/features/feed-strategies/components/feed-strategies-data-grid';
import { UpdateFeedStrategyInput } from 'views/profiles-strategies/features/feed-strategies/types/update-feed-strategy-input.type';
import ProfilesStrategiesTabHeader from '../../../components/profile-strategies-tabs-header';
import { postFeedStrategy } from '../api/create-feed-strategy';
import { getProductsArticles, getProductsCustomerAssortment } from '../api/get-products';
import { checkDuplicateProfiles, checkStartWeight } from '../utils/strategy-validation';

type Props = {};

type Row = {
  id: string;
  fromWeight: number;
  product: { id: string; label: string };
};

type ProductFiltered = {
  id: string;
  label: string;
};

const CreateFeedStrategies: React.FC<Props> = () => {
  const intl = useIntl();
  const history = useHistory();
  const theme = useTheme();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const { errorDictionary, changeErrorDictionary, handleEditing, isEditing, hasError } = useDataGridError();

  const [rows, setRows] = useState<GridRowsProp<Row>>([]);
  const [productLines, setProductLines] = useState<ProductFiltered[]>([]);

  const getOwnerTypeId = () => {
    switch (ownerType) {
      case OwnerType.MARKET:
        return account.company?.marketId;
      case OwnerType.COMPANY:
        return account.company?.companyId;
      case OwnerType.SITE:
        return siteId;
    }
  };

  // STATE
  const [duplicateLines, setDuplicateLines] = useState<boolean>(false);
  const [ownerType, setOwnerType] = useState<OwnerType>(OwnerType.MARKET);
  const [startWeightError, setStartWeightError] = useState<boolean>(false);
  const [siteId, setSiteId] = useState(_.first(account.company!.addressAccess?.filter((site) => site.seaCloudId))?.id);

  // FORMS
  const { handleSubmit, control } = useForm<UpdateFeedStrategyInput>({
    defaultValues: {
      name: '',
      active: false,
      isTemporary: false,
      lines: [
        {
          fromWeight: 0,
          productNumber: '',
        },
      ],
    },
    shouldUnregister: false,
  });

  const { data: products } = useGet(
    ['getProductsArticles', ownerType, account.company!.customerErpId],
    (context) =>
      context.queryKey[1] === OwnerType.MARKET ? getProductsArticles() : getProductsCustomerAssortment(String(context.queryKey[2])),
    {
      enabled: !!account.company?.customerErpId && !!ownerType,
    }
  );

  useEffect(() => {
    if (!products) return;

    setProductLines(
      products
        .filter((p) => p.ItemName !== null)
        .sort((a, b) => a.ItemName.toLowerCase().localeCompare(b.ItemName.toLowerCase()))
        .map((p) => ({ id: String(p.ItemNumber), label: `${p.ItemName} (${Number(p.ItemNumber)})` }))
    );
  }, [products]);

  // POST DATA
  const createFeedStrategy = usePost<number, UpdateFeedStrategyInput>(
    (request) => postFeedStrategy(ownerType, String(getOwnerTypeId()), request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      history.push(`/profiles-strategies/feed-strategies/${response}`);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const handleCreateFeedStrategy = (request: UpdateFeedStrategyInput) => {
    let data = {
      ...request,
      lines: rows.map((line) => {
        return {
          fromWeight: line.fromWeight,
          productNumber: line.product?.id ?? '',
        };
      }),
    };
    createFeedStrategy.mutate(data);
  };

  useEffect(() => {
    setStartWeightError(checkStartWeight(rows as Row[]));
    setDuplicateLines(checkDuplicateProfiles(rows as Row[]));
  }, [rows]);

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return row.product?.id === '';
    });
  }, [rows]);

  return (
    <>
      <ProfilesStrategiesTabHeader utils={<Breadcrumbs />} />
      <form onSubmit={handleSubmit(handleCreateFeedStrategy)}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Typography variant="h2" pb={4} color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.generalTitle`} />
              </Typography>
              <Paper>
                <TextField
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `profilesStrategies.label.name` })}
                  controller={{
                    name: 'name',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                />
                <Grid>
                  <ToggleButton
                    dark
                    label={intl.formatMessage({ id: `profilesStrategies.label.feedStrategyActive` })}
                    controller={{
                      name: 'active',
                      control: control,
                    }}
                  />
                  <ToggleButton
                    dark
                    label={intl.formatMessage({ id: `profilesStrategies.label.feedStrategyTemporary` })}
                    controller={{
                      name: 'isTemporary',
                      control: control,
                    }}
                  />
                </Grid>
              </Paper>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Typography variant="h2" pb={4} color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.ownerTitle`} />
              </Typography>
              <Paper>
                <Select
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `profilesStrategies.ownerTitle` })}
                  value={ownerType}
                  onChange={(event) => setOwnerType(event.target.value as OwnerType)}>
                  <RadioSelectItem value={OwnerType.MARKET} selected={OwnerType.MARKET === ownerType}>
                    <FormattedMessage id={`profilesStrategies.menuItem.market`} />
                  </RadioSelectItem>
                  <RadioSelectItem value={OwnerType.COMPANY} selected={OwnerType.COMPANY === ownerType}>
                    <FormattedMessage id={`profilesStrategies.menuItem.company`} />
                  </RadioSelectItem>
                  <RadioSelectItem value={OwnerType.SITE} selected={OwnerType.SITE === ownerType}>
                    <FormattedMessage id={`profilesStrategies.menuItem.site`} />
                  </RadioSelectItem>
                </Select>

                {ownerType === OwnerType.SITE && (
                  <Select
                    dark
                    fullWidth
                    label="Site"
                    margin="none"
                    value={siteId}
                    onChange={(event) => setSiteId(String(event.target.value))}>
                    {_.chain(account.company?.addressAccess)
                      .sortBy((s) => s.name.toLowerCase())
                      .value()
                      .map((site) => (
                        <RadioSelectItem key={site.id} value={site.id} selected={site.id === siteId}>
                          {site.name}
                        </RadioSelectItem>
                      ))}
                  </Select>
                )}
              </Paper>
            </Paper>
          </Grid>
          <Grid size={12}>
            {!!duplicateLines && (
              <Alert severity="error">
                <FormattedMessage id={`profilesStrategies.createFeedStrategies.duplicateProfiles`} />
              </Alert>
            )}
            {!!startWeightError && (
              <Alert severity="error">
                <FormattedMessage id={`profilesStrategies.createFeedStrategies.startWeightValue`} />
              </Alert>
            )}
            <Paper transparent>
              <Typography variant="h2" pb={4} color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.linesTitle`} />
              </Typography>
              <Box>
                <FeedStrategiesGrid
                  rows={rows}
                  productLines={productLines}
                  setRows={setRows}
                  handleEditing={handleEditing}
                  errorDictionary={errorDictionary}
                  changeErrorDictionary={changeErrorDictionary}
                />
              </Box>
            </Paper>
          </Grid>
          <Box display="flex" justifyContent="end" width={'100%'} pt={4}>
            <Button
              type="submit"
              disabled={
                createFeedStrategy.isPending || disableSubmit || !!duplicateLines || startWeightError || isEditing || hasError
              }>
              {createFeedStrategy.isPending ? (
                <CircularProgress color="inherit" />
              ) : (
                <FormattedMessage id="general.submit" values={{ type: '' }} />
              )}
            </Button>
          </Box>
        </Grid>
      </form>
    </>
  );
};

export default CreateFeedStrategies;
